import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Quote } from 'lucide-react';
import AnimatedSection from './AnimatedSection';
import { useNavigate } from 'react-router-dom';

const FeedbackGallerySection = () => {
  const navigate = useNavigate();

  const handleStartTrial = () => navigate('/trial-request');
  const handleViewResources = () => navigate('/resource-library');
  // Featured feedback WITH images (top tier)
  const featuredFeedback = [
    {
      name: "ICU Nurse",
      title: "New Hampshire",
      hospital: "",
      quote: "Using them on a crrt patient! The educator is super excited!",
      image: "/images/testimonials/crrt_cordflex_img.webp"
    },
    {
      name: "Supply Chain Manager",
      title: "Texas",
      hospital: "",
      quote: "You don't have to sell me, I like the Cordflex",
      image: "/images/testimonials/cordflex_ny.webp",
      objectPosition: "center top"
    },
    {
      name: "Nurse Educator",
      title: "Florida",
      hospital: "",
      quote: "I've been talking about this product for over a year and showing it to different people and everybody likes it",
      image: "/images/testimonials/cordflex_testimonial.webp"
    }
  ];

  // Text-only feedback (scrolling marquee)
  const textFeedback = [
    {
      name: "ICU Nurse",
      title: "Survey Respondent",
      hospital: "",
      quote: "I had a mass transfusion and it was a bloody mess and [the lines] kept getting tangled and disconnecting"
    },
    {
      name: "ICU Nurse",
      title: "Survey Respondent",
      hospital: "",
      quote: "There have been times where we have had to carry pumps to the bathroom because the cords are so tangled that we cannot separate them"
    },
    {
      name: "Charge Nurse",
      title: "Survey Respondent",
      hospital: "",
      quote: "Organizing lines is one of the most time-consuming tasks in the ICU"
    },
    {
      name: "ICU Nurse",
      title: "Survey Respondent",
      hospital: "",
      quote: "I think that this is a great idea. It would be really nice for CRRT patients who have a lot of lines"
    },
    {
      name: "Clinical Educator",
      title: "Survey Respondent",
      hospital: "",
      quote: "Even with labeling, it's hard to tell them apart when everything is tangled together"
    },
    {
      name: "ICU Nurse",
      title: "Survey Respondent",
      hospital: "",
      quote: "I have seen bloody messes from lines being pulled or disconnected accidentally"
    },
    {
      name: "Nurse Manager",
      title: "Survey Respondent",
      hospital: "",
      quote: "Nurses spend a significant amount of time managing lines that could be spent on patient care"
    },
    {
      name: "ICU Nurse",
      title: "Survey Respondent",
      hospital: "",
      quote: "I would love something to organize lines better—especially during emergencies when every second counts"
    },
    {
      name: "Staff Nurse",
      title: "Survey Respondent",
      hospital: "",
      quote: "I've accidentally pulled out lines before when trying to untangle them. It's a real patient safety issue"
    },
    {
      name: "ICU Nurse",
      title: "Survey Respondent",
      hospital: "",
      quote: "We need a better system. Taping lines to the bed rail doesn't cut it anymore"
    },
    {
      name: "Clinical Nurse",
      title: "Survey Respondent",
      hospital: "",
      quote: "During a code, tangled lines can delay critical interventions. Anything that helps organize them is a win"
    },
    {
      name: "ICU Nurse",
      title: "Survey Respondent",
      hospital: "",
      quote: "Ambulating patients with multiple drips is a nightmare. The lines get caught on everything"
    }
  ];

  const QuoteCard = ({ item }) => (
    <div className="flex-shrink-0 w-80 mx-3">
      <Card className="shadow-lg h-full bg-white">
        <CardContent className="p-6 flex flex-col h-full">
          <Quote className="h-8 w-8 mb-3 opacity-30" style={{ color: '#214140' }} />
          <blockquote className="text-gray-700 italic mb-6 leading-relaxed flex-grow">
            "{item.quote}"
          </blockquote>
          <div className="pt-4 border-t border-gray-300/50">
            <div className="font-semibold text-sm" style={{ color: '#214140' }}>
              {item.name}
            </div>
            <div className="text-xs text-gray-600">
              {item.title}
            </div>
            <div className="text-xs text-gray-500">
              {item.hospital}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Duplicate the array for seamless loop
  const scrollItems = [...textFeedback, ...textFeedback];

  return (
    <section className="py-20" style={{ backgroundColor: '#DFEAF0' }}>
      <div className="container mx-auto px-4">
        <AnimatedSection animation="fade-up">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#214140' }}>
              Trusted by Healthcare Professionals
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See what ICU nurses and hospital staff are saying about Cordflex
            </p>
          </div>
        </AnimatedSection>

        {/* Featured Feedback with Images */}
        <AnimatedSection animation="fade-up" delay={200}>
          <div className="grid md:grid-cols-3 gap-6 mb-16 max-w-6xl mx-auto">
            {featuredFeedback.map((item, index) => (
              <Card key={index} className="bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                  <img
                    src={item.image}
                    alt={`Cordflex in use - ${item.name}`}
                    className="w-full h-full object-cover"
                    style={item.objectPosition ? { objectPosition: item.objectPosition } : undefined}
                  />
                </div>
                <CardContent className="p-5">
                  <blockquote className="text-gray-700 italic mb-4 leading-relaxed text-sm">
                    "{item.quote}"
                  </blockquote>
                  <div className="pt-3 border-t border-gray-100">
                    <div className="font-semibold text-sm" style={{ color: '#214140' }}>
                      {item.name}
                    </div>
                    <div className="text-xs text-gray-600">
                      {item.title}
                    </div>
                    {item.hospital && (
                      <div className="text-xs text-gray-500">
                        {item.hospital}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </AnimatedSection>

        {/* Auto-Scrolling Quotes Marquee 
        <AnimatedSection animation="fade-up" delay={400}>
          <style>{`
            @keyframes marquee-scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .marquee-track {
              animation: marquee-scroll 70s linear infinite;
            }
            .marquee-track:hover {
              animation-play-state: paused;
            }
          `}</style>
          <div className="overflow-hidden">
            <div className="marquee-track flex" style={{ width: 'max-content' }}>
              {scrollItems.map((item, index) => (
                <QuoteCard key={index} item={item} />
              ))}
            </div>
          </div>
        </AnimatedSection>
*/}

        {/* CTA */}
        <AnimatedSection animation="fade-up" delay={500}>
          <div className="text-center mt-16">
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#214140' }}>
              Experience the Same Results
            </h3>
            <p className="text-gray-600 mb-8">
              Join hundreds of healthcare professionals who trust Cordflex
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleStartTrial}
                size="lg"
                className="px-8 py-4 text-lg font-semibold hover:opacity-90 transition-all duration-200 transform hover:scale-105"
                style={{ backgroundColor: '#214140', color: 'white' }}
              >
                Request a Trial
              </Button>
              <Button
                onClick={handleViewResources}
                variant="outline"
                size="lg"
                className="px-8 py-4 text-lg font-semibold border-2 hover:bg-teal-50 transition-all duration-200 transform hover:scale-105"
                style={{ borderColor: '#214140', color: '#214140' }}
              >
                View Resource Library
              </Button>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default FeedbackGallerySection;
